import {parseMoney, sanitizeMoneyInput} from '@sollapay/utils';
import {useEffect, useState} from 'react';

import {FieldInput} from '../FieldInput';

import type {FieldInputProps} from '../FieldInput';
import type {ChangeEvent, FocusEvent} from 'react';

export interface FieldMoneyInputProps extends Omit<
  FieldInputProps,
  'value' | 'onChange' | 'inputMode' | 'type' | 'startAdornment'
> {
  value: string;
  onChange: (raw: string) => void;
}

const CURRENCY_ADORNMENT = <span className="text-sm font-medium text-fg-tertiary">₪</span>;

const formatDisplay = (raw: string): string => {
  if (!raw) return '';
  const dot = raw.indexOf('.');
  if (dot === -1) return parseMoney(raw).formatNIS({symbol: false});
  const int = raw.slice(0, dot);
  const dec = raw.slice(dot + 1);
  const formattedInt = int ? parseMoney(int).formatNIS({symbol: false}) : '';
  if (!dec) return `${formattedInt}.`;
  const trimmedDec = dec.replace(/0+$/, '');
  return trimmedDec ? `${formattedInt}.${trimmedDec}` : formattedInt;
};

export function FieldMoneyInput({
  value,
  onChange,
  onBlur,
  ...fieldInputProps
}: FieldMoneyInputProps) {
  const [displayValue, setDisplayValue] = useState(() => formatDisplay(value));

  useEffect(() => {
    setDisplayValue(formatDisplay(value));
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeMoneyInput(e.target.value);
    onChange(sanitized);
    setDisplayValue(formatDisplay(sanitized));
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (value.endsWith('.')) {
      const trimmed = value.slice(0, -1);
      onChange(trimmed);
      setDisplayValue(formatDisplay(trimmed));
    }
    onBlur?.(e);
  };

  return (
    <FieldInput
      {...fieldInputProps}
      startAdornment={CURRENCY_ADORNMENT}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      inputMode="decimal"
    />
  );
}
